<script setup lang="ts">
/**
 * Perfil propio, con el layout de la vista de usuario de la plantilla
 * (`pages/apps/user/view/[id].vue`): ficha a la izquierda, pestañas a la
 * derecha.
 *
 * Antes era un formulario permanente. El 95 % de las visitas es para *mirar*
 * —qué mail tengo, qué rol, qué puesto a bordo—, y para eso un formulario es el
 * peor formato. Ahora la ficha es la portada, y editar es una pestaña al lado.
 *
 * La contraseña vive en la pestaña Security y ya no en el menú: cambiarla es
 * editar tu perfil, no visitar una sección del portal.
 */
import { useRoute, useRouter } from 'vue-router'
import { changePassword, deleteAvatar, updateProfile, uploadAvatar } from '@/api/account'
import { useAuthStore } from '@/stores/auth'
import ProviderActivityTimeline, { type TimelineEntry } from '@/views/settings/ProviderActivityTimeline.vue'
import ProviderBioPanel from '@/views/settings/ProviderBioPanel.vue'
import { listDiveCenters } from '@/api/dive-centers'
import { apiMessage, notifyError, notifySuccess } from '@/composables/useSnackbar'
import { useCertificationAuthorities } from '@/composables/useCertificationAuthorities'

definePage({ meta: { requiresAuth: true } })

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

// Mismas listas que el alta de un miembro del equipo, para que el mismo puesto
// se escriba igual venga de donde venga.
const JOB_TITLES = ['Instructor', 'Divemaster', 'Dive guide', 'Captain', 'Boat crew', 'Front desk']
const CERT_LEVELS = ['Open Water', 'Advanced', 'Rescue', 'Divemaster', 'Instructor']

// La lista de agencias sale del backend, la misma que consume la app de buzos.
const { codes: agencyCodes } = useCertificationAuthorities()

const tabs = [
  { icon: 'ri-user-line', title: 'Account' },
  { icon: 'ri-lock-2-line', title: 'Security' },
]

const currentTab = ref(0)

/*
 * La derecha arranca en **lectura**: entrar al perfil es mirarlo, no editarlo.
 * Con el formulario siempre desplegado, la pantalla proponía cambiar datos
 * cuando lo que se venía a hacer era consultarlos.
 */
const editing = ref(false)

const timeline = ref<TimelineEntry[]>([])

// Mismo archivo que el avatar de la barra superior, para que un usuario sin
// foto se vea igual arriba y acá.
const DEFAULT_AVATAR = '/images/avatar-default.png'

const avatarInput = ref<HTMLInputElement>()
const avatarBusy = ref(false)

/**
 * Sube la foto y **repinta con la URL que devuelve el backend**, ya firmada.
 * Releer `/account/me` solo para conseguir un token válido sería un viaje de
 * más justo cuando el usuario está esperando ver su cara.
 */
async function changeAvatar(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0]
  if (!file)
    return

  avatarBusy.value = true
  try {
    await uploadAvatar(file)
    await auth.refreshMe()
    notifySuccess('Photo updated.')
  }
  catch (err) {
    notifyError(apiMessage(err, 'Could not upload the photo.'))
  }
  finally {
    avatarBusy.value = false
    // Se limpia el input o volver a elegir el MISMO archivo no dispara `change`.
    if (avatarInput.value)
      avatarInput.value.value = ''
  }
}

async function resetAvatar() {
  avatarBusy.value = true
  try {
    await deleteAvatar()
    await auth.refreshMe()
    notifySuccess('Photo removed.')
  }
  catch (err) {
    notifyError(apiMessage(err, 'Could not remove the photo.'))
  }
  finally {
    avatarBusy.value = false
  }
}

const firstName = ref('')
const lastName = ref('')
const jobTitle = ref('')
const certificationLevel = ref('')
const phone = ref('')
const agency = ref('')
const saving = ref(false)

// Security
const isCurrentPasswordVisible = ref(false)
const isNewPasswordVisible = ref(false)
const isConfirmPasswordVisible = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const pwdSaving = ref(false)

const passwordRequirements = [
  'Minimum 6 characters long — the more, the better',
  'At least one uppercase character',
  'Changing it signs you out on every device',
]

const user = computed(() => auth.providerUser)

function resetForm() {
  const u = user.value
  if (!u)
    return

  firstName.value = u.firstName
  lastName.value = u.lastName
  jobTitle.value = u.staffProfile?.jobTitle ?? ''
  certificationLevel.value = u.staffProfile?.certificationLevel ?? ''
  agency.value = u.staffProfile?.agency ?? ''
  phone.value = u.staffProfile?.phone ?? ''
}

onMounted(async () => {
  await auth.refreshMe()
  resetForm()
  buildTimeline()

  /*
   * `/settings/security` redirige acá con `?tab=security`. Sin esto, un enlace
   * guardado de la versión anterior caía en la ficha sin decir a qué venía, y
   * además hay que abrir la edición: la contraseña vive ahí adentro.
   */
  if (route.query.tab === 'security') {
    currentTab.value = 1
    editing.value = true
  }
})

/**
 * Hechos reales de la cuenta, sin petición nueva más allá de los dive centers.
 * El límite está documentado en `ProviderActivityTimeline`: son hechos de la
 * cuenta, no acciones atribuidas a esta persona.
 */
async function buildTimeline() {
  const entries: TimelineEntry[] = []
  const u = auth.providerUser
  const p = auth.provider

  if (u?.lastLoginAt)
    entries.push({ title: 'Signed in', text: 'Most recent sign-in to the portal.', at: u.lastLoginAt, color: 'primary' })

  if (p?.verifiedAt)
    entries.push({ title: 'Account verified', text: 'Blue Trails approved this account.', at: p.verifiedAt, color: 'success' })

  if (u?.createdAt)
    entries.push({ title: 'Joined the account', text: `You were added as ${u.role}.`, at: u.createdAt, color: 'info' })

  if (p?.createdAt)
    entries.push({ title: 'Account created', text: p.displayName, at: p.createdAt, color: 'secondary' })

  try {
    const res = await listDiveCenters()

    for (const dc of res.diveCenters) {
      if (dc.createdAt)
        entries.push({ title: `Dive center added: ${dc.name}`, text: dc.address?.city || undefined, at: dc.createdAt, color: 'warning' })
    }
  }
  catch { /* la línea de tiempo es contexto: sin los centros igual sirve */ }

  timeline.value = entries
}

function cancelEdit() {
  editing.value = false
  resetForm()
  if (route.query.tab)
    router.replace({ query: {} })
}

async function save() {
  saving.value = true
  try {
    await updateProfile({
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim(),
      /*
       * Se manda entero y no solo lo que cambió: el backend reemplaza el
       * sub-documento, así que enviar una parte borraría el resto. Los campos
       * que este formulario no muestra se preservan explícitamente.
       */
      staffProfile: {
        ...(user.value?.staffProfile ?? {}),
        jobTitle: jobTitle.value.trim() || undefined,
        certificationLevel: certificationLevel.value || undefined,
        agency: agency.value.trim() || undefined,
        phone: phone.value.trim() || undefined,
      },
    })
    await auth.refreshMe()
    notifySuccess('Profile saved.')
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not save.'))
  }
  finally {
    saving.value = false
  }
}

async function savePassword() {
  if (!currentPassword.value || !newPassword.value) {
    notifyError('All password fields are required.')

    return
  }
  if (newPassword.value !== confirmPassword.value) {
    notifyError('New passwords do not match.')

    return
  }
  if (newPassword.value.length < 6 || !/[A-Z]/.test(newPassword.value)) {
    notifyError('Password must be at least 6 characters and include one uppercase letter.')

    return
  }

  pwdSaving.value = true
  try {
    await changePassword(currentPassword.value, newPassword.value)
    // El backend invalidó todos los refresh tokens — hay que loguearse de nuevo.
    notifySuccess('Password changed. Sign in again.')
    auth.logout()
    router.push({ name: 'login' })
  }
  catch (e) {
    notifyError(apiMessage(e, 'Could not change password.'))
  }
  finally {
    pwdSaving.value = false
  }
}

function resetPassword() {
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
}
</script>

<template>
  <div>
    <VRow>
      <VCol
        cols="12"
        md="5"
        lg="4"
      >
        <ProviderBioPanel
          :user="user ?? null"
          @edit="editing = true; currentTab = 0"
        />
      </VCol>

      <VCol
        cols="12"
        md="7"
        lg="8"
      >
        <!-- Lectura: qué pasó en la cuenta. -->
        <ProviderActivityTimeline
          v-if="!editing"
          :entries="timeline"
        />

        <VTabs
          v-else
          v-model="currentTab"
          class="v-tabs-pill"
        >
          <VTab
            v-for="tab in tabs"
            :key="tab.icon"
          >
            <VIcon
              start
              :icon="tab.icon"
            />
            <span>{{ tab.title }}</span>
          </VTab>
        </VTabs>

        <VWindow
          v-if="editing"
          v-model="currentTab"
          class="mt-6 disable-tab-transition"
          :touch="false"
        >
          <!-- 👉 Account -->
          <VWindowItem>
            <VCard title="Account details">
              <VCardText>
                <!-- 👉 Avatar (markup de `AccountSettingsAccount.vue`) -->
                <div class="d-flex mb-10">
                  <VAvatar
                    rounded
                    size="100"
                    class="me-6"
                    :image="user?.avatarUrl || DEFAULT_AVATAR"
                  />

                  <form class="d-flex flex-column justify-center gap-4">
                    <div class="d-flex flex-wrap gap-4">
                      <VBtn
                        color="primary"
                        :loading="avatarBusy"
                        @click="avatarInput?.click()"
                      >
                        <VIcon
                          icon="ri-upload-cloud-line"
                          class="d-sm-none"
                        />
                        <span class="d-none d-sm-block">Upload new photo</span>
                      </VBtn>
                      <input
                        ref="avatarInput"
                        type="file"
                        name="file"
                        accept=".jpeg,.png,.jpg,.webp,.gif"
                        hidden
                        @change="changeAvatar"
                      >
                      <VBtn
                        type="reset"
                        color="error"
                        variant="outlined"
                        :disabled="!user?.avatarUrl || avatarBusy"
                        @click.prevent="resetAvatar"
                      >
                        <span class="d-none d-sm-block">Reset</span>
                        <VIcon
                          icon="ri-refresh-line"
                          class="d-sm-none"
                        />
                      </VBtn>
                    </div>
                    <p class="text-body-1 mb-0">
                      Allowed JPG, GIF or PNG. Max size of 800K
                    </p>
                  </form>
                </div>

                <VForm @submit.prevent="save">
                  <VRow>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="firstName"
                        label="First Name"
                        placeholder="Eduardo"
                        :disabled="saving"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="lastName"
                        label="Last Name"
                        placeholder="Villa"
                        :disabled="saving"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        :model-value="user?.email"
                        label="E-mail"
                        type="email"
                        readonly
                        disabled
                        hint="Your email is your sign-in identifier and cannot be changed here."
                        persistent-hint
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="phone"
                        label="Phone Number"
                        placeholder="+34 600 000 000"
                        :disabled="saving"
                      />
                    </VCol>

                    <VCol cols="12">
                      <h6 class="text-h6 text-medium-emphasis">
                        On board
                      </h6>
                      <p class="text-body-2 text-medium-emphasis mb-0">
                        What you do on an activity. It has nothing to do with your portal
                        permissions — it is what shows up when someone assigns you to a dive
                        in the calendar.
                      </p>
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VCombobox
                        v-model="jobTitle"
                        :items="JOB_TITLES"
                        label="Role on board"
                        placeholder="Captain"
                        :disabled="saving"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VSelect
                        v-model="certificationLevel"
                        :items="CERT_LEVELS"
                        label="Certification level"
                        placeholder="Instructor"
                        clearable
                        :disabled="saving"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VSelect
                        v-model="agency"
                        :items="agencyCodes"
                        label="Agency"
                        placeholder="PADI"
                        clearable
                        :disabled="saving"
                      />
                    </VCol>

                    <!-- 👉 Form Actions -->
                    <VCol
                      cols="12"
                      class="d-flex flex-wrap gap-4"
                    >
                      <VBtn
                        type="submit"
                        :loading="saving"
                      >
                        Save changes
                      </VBtn>

                      <VBtn
                        color="secondary"
                        variant="outlined"
                        type="reset"
                        :disabled="saving"
                        @click.prevent="cancelEdit"
                      >
                        Cancel
                      </VBtn>
                    </VCol>
                  </VRow>
                </VForm>
              </VCardText>
            </VCard>
          </VWindowItem>

          <!-- 👉 Security -->
          <VWindowItem>
            <VCard>
              <VCardItem class="pb-6">
                <VCardTitle>Change Password</VCardTitle>
              </VCardItem>
              <VForm @submit.prevent="savePassword">
                <VCardText class="pt-0">
                  <VRow>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="currentPassword"
                        :type="isCurrentPasswordVisible ? 'text' : 'password'"
                        :append-inner-icon="isCurrentPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                        autocomplete="current-password"
                        label="Current Password"
                        placeholder="············"
                        :disabled="pwdSaving"
                        @click:append-inner="isCurrentPasswordVisible = !isCurrentPasswordVisible"
                      />
                    </VCol>
                  </VRow>

                  <VRow>
                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="newPassword"
                        :type="isNewPasswordVisible ? 'text' : 'password'"
                        :append-inner-icon="isNewPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                        label="New Password"
                        autocomplete="new-password"
                        placeholder="············"
                        :disabled="pwdSaving"
                        @click:append-inner="isNewPasswordVisible = !isNewPasswordVisible"
                      />
                    </VCol>

                    <VCol
                      cols="12"
                      md="6"
                    >
                      <VTextField
                        v-model="confirmPassword"
                        :type="isConfirmPasswordVisible ? 'text' : 'password'"
                        :append-inner-icon="isConfirmPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                        autocomplete="new-password"
                        label="Confirm New Password"
                        placeholder="············"
                        :disabled="pwdSaving"
                        @click:append-inner="isConfirmPasswordVisible = !isConfirmPasswordVisible"
                      />
                    </VCol>
                  </VRow>
                </VCardText>

                <VCardText>
                  <h6 class="text-h6 text-medium-emphasis mt-1">
                    Password Requirements:
                  </h6>

                  <VList>
                    <VListItem
                      v-for="(item, index) in passwordRequirements"
                      :key="index"
                      class="px-0"
                    >
                      <template #prepend>
                        <VIcon
                          size="8"
                          icon="ri-circle-fill"
                          color="rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity))"
                        />
                      </template>
                      <VListItemTitle class="text-medium-emphasis text-wrap">
                        {{ item }}
                      </VListItemTitle>
                    </VListItem>
                  </VList>

                  <div class="d-flex flex-wrap gap-4">
                    <VBtn
                      type="submit"
                      :loading="pwdSaving"
                    >
                      Save changes
                    </VBtn>

                    <VBtn
                      type="reset"
                      color="secondary"
                      variant="outlined"
                      :disabled="pwdSaving"
                      @click.prevent="resetPassword"
                    >
                      Reset
                    </VBtn>
                  </div>
                </VCardText>
              </VForm>
            </VCard>
          </VWindowItem>
        </VWindow>
      </VCol>
    </VRow>
  </div>
</template>
